import { motion } from "framer-motion";
import StatusPill from "./StatusPill";

/**
 * TimelineList — vertical timeline com dots coloridos por status.
 *
 * Props:
 *   items: [{
 *     id?: string,
 *     time?: string,           // "09:30" — texto à esquerda
 *     title: string,           // texto principal
 *     subtitle?: string,       // linha secundária
 *     status?: 'info'|'success'|'warning'|'danger'|'neutral',
 *     type?: string,           // 'gap' renderiza linha itálica leve, qualquer outro = item
 *     badge?: string,          // texto opcional do StatusPill à direita
 *     onAction?: () => void,   // callback do botão de ação (renderiza só se passado)
 *     actionLabel?: string,    // texto do botão (default "concluir")
 *     done?: boolean,          // aplica opacity 50 + line-through no title
 *   }]
 */
const DOT_COLORS = {
  info:    "bg-semantic-info",
  success: "bg-semantic-success",
  warning: "bg-semantic-warning",
  danger:  "bg-semantic-danger",
  neutral: "bg-slate-500",
};

function GapItem({ title, subtitle }) {
  return (
    <div className="relative pl-7 py-2 text-[11px] text-slate-500 italic">
      <span className="absolute left-[7px] top-0 bottom-0 w-px bg-white/10" aria-hidden />
      <span>{title}</span>
      {subtitle ? <span className="text-slate-600"> · {subtitle}</span> : null}
    </div>
  );
}

function TimelineItem({ item, isLast, idx }) {
  if (item.type === "gap") return <GapItem title={item.title} subtitle={item.subtitle} />;

  const dot = DOT_COLORS[item.status || "info"];
  return (
    <motion.div
      initial={{ opacity: 0, x: -4 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.2, delay: Math.min(idx * 0.03, 0.15) }}
      className="relative pl-7 py-2 group"
      data-testid="ds-timeline-item"
    >
      {/* Linha vertical contínua */}
      {!isLast ? (
        <span className="absolute left-[7px] top-3 bottom-0 w-px bg-white/8" aria-hidden />
      ) : null}
      {/* Dot */}
      <span
        className={`absolute left-[3px] top-2.5 w-2.5 h-2.5 rounded-full ${dot} ring-2 ring-nectar-card`}
        aria-hidden
      />

      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            {item.time ? (
              <span className="text-[11px] tabular-nums text-slate-500 shrink-0">
                {item.time}
              </span>
            ) : null}
            <p className={`text-sm leading-tight ${item.done ? "text-slate-500 line-through" : "text-slate-200"}`}>
              {item.title}
            </p>
          </div>
          {item.subtitle ? (
            <p className="text-[11px] text-slate-500 mt-0.5">{item.subtitle}</p>
          ) : null}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {item.badge ? <StatusPill tone={item.status}>{item.badge}</StatusPill> : null}
          {item.onAction ? (
            <button
              type="button"
              onClick={item.onAction}
              className="opacity-0 group-hover:opacity-100 transition-opacity text-[11px] text-brand-cyan/80 hover:text-brand-cyan px-2 py-0.5 rounded-md hover:bg-brand-cyan/10"
              data-testid="ds-timeline-action"
            >
              {item.actionLabel || "concluir"}
            </button>
          ) : null}
        </div>
      </div>
    </motion.div>
  );
}

export default function TimelineList({ items = [] }) {
  if (!items.length) {
    return <p className="text-[11px] text-slate-500 text-center py-3">Nada agendado</p>;
  }
  return (
    <div className="relative" data-testid="ds-timeline-list">
      {items.map((item, idx) => (
        <TimelineItem
          key={item.id ?? idx}
          item={item}
          idx={idx}
          isLast={idx === items.length - 1}
        />
      ))}
    </div>
  );
}
